/**
 * Narrative Manager
 * Manages narrative display flow and timing
 */

import { GameState, StageId } from '../models/GameState';
import { PlayerRole } from '../models/Player';
import { contentLoader, Narrative } from './ContentLoader';
import { STAGE_VIDEOS, VideoCondition } from './videoConfig';

export interface NarrativeSequence {
  narratives: Narrative[];
  autoAdvance: boolean;
  delayMs?: number;
}

export class NarrativeManager {
  /**
   * Evaluate whether a video condition is met based on game state
   */
  private evaluateVideoCondition(
    condition: VideoCondition | undefined,
    gameState?: GameState
  ): boolean {
    if (!condition) return true;
    if (!gameState) return false;

    if (condition.playerRole) {
      const player = gameState.players.find(
        p => p.role === condition.playerRole
      );
      if (!player) return false;
      const answer = player.decisions.get(condition.questionId);
      return answer != null && condition.answers.includes(String(answer));
    }

    // Check any player's decision for this question (team-level decisions)
    for (const player of gameState.players) {
      const answer = player.decisions.get(condition.questionId);
      if (answer != null && condition.answers.includes(String(answer))) {
        return true;
      }
    }

    // Also check team decisions array
    const teamDecision = gameState.decisions.find(
      d => d.questionId === condition.questionId
    );
    if (teamDecision?.finalAnswer != null) {
      return condition.answers.includes(String(teamDecision.finalAnswer));
    }

    return false;
  }

  /**
   * Attach video URLs from STAGE_VIDEOS config to matching narratives
   */
  private attachVideos(
    narratives: Narrative[],
    stage: StageId,
    timing: 'onEntry' | 'onExit',
    gameState?: GameState
  ): void {
    const stageVideos = STAGE_VIDEOS[stage]?.filter(v => v.timing === timing) || [];
    for (const video of stageVideos) {
      if (video.narrativeIndex === undefined) continue;
      if (video.narrativeIndex >= narratives.length) continue;
      if (!this.evaluateVideoCondition(video.condition, gameState)) continue;
      narratives[video.narrativeIndex].videoUrl = video.url;
      if (video.videoOnly) {
        narratives[video.narrativeIndex].videoOnly = true;
      }
    }
  }

  /**
   * Get narratives for stage entry (enhanced with narrative sequences)
   */
  getStageEntryNarratives(stage: StageId, variant?: 'A' | 'B', gameState?: GameState): Narrative[] {
    // Try to get enhanced narrative sequences first
    const narrativeSegments = contentLoader.getNarrativeSegments(stage, 'onEntry');

    if (narrativeSegments.length > 0) {
      // Convert narrative segments to Narrative format
      const narratives = narrativeSegments.map(segment =>
        contentLoader.narrativeSegmentToNarrative(segment)
      );
      this.attachVideos(narratives, stage, 'onEntry', gameState);
      return narratives;
    }

    // Fallback to original JSON-based narratives
    const narratives: Narrative[] = [];

    switch (stage) {
      case 0:
        const opening = contentLoader.getStageNarrative(0, 'opening');
        if (opening) narratives.push(opening);
        break;

      case 1:
        const arrival = contentLoader.getStageNarrative(1, 'arrival');
        const aftermath = contentLoader.getStageNarrative(1, 'aftermath');
        if (arrival) narratives.push(arrival);
        if (aftermath) narratives.push(aftermath);
        break;

      case 2:
        const caveArrival = contentLoader.getStageNarrative(2, 'caveArrival');
        if (caveArrival) narratives.push(caveArrival);
        break;

      case 3:
        const descent = contentLoader.getStageNarrative(3, 'descent');
        if (descent) narratives.push(descent);
        break;

      case 4:
        const missionTime = contentLoader.getStageNarrative(4, 'missionTime');
        if (missionTime) narratives.push(missionTime);
        break;

      case 5:
        if (variant === 'A') {
          const normalAscent = contentLoader.getStageNarrative(5, 'normalAscent');
          if (normalAscent) narratives.push(normalAscent);
        } else {
          const fuelCrisis = contentLoader.getStageNarrative(5, 'fuelCrisis');
          if (fuelCrisis) narratives.push(fuelCrisis);
        }
        break;
    }

    this.attachVideos(narratives, stage, 'onEntry', gameState);
    return narratives;
  }

  /**
   * Get narratives for a specific step
   */
  getStepNarratives(
    stage: StageId,
    step: string,
    _gameState: GameState
  ): Narrative[] {
    const narratives: Narrative[] = [];

    if (stage === 0) {
      switch (step) {
        case '0.2':
          const teamIntro = contentLoader.getStageNarrative(0, 'teamIntro');
          if (teamIntro) narratives.push(teamIntro);
          break;

        case '0.3':
          const containerSelection = contentLoader.getStageNarrative(0, 'containerSelection');
          const gee = contentLoader.getStageNarrative(0, 'geeIntervention');
          const doc = contentLoader.getStageNarrative(0, 'docIntervention');
          if (containerSelection) narratives.push(containerSelection);
          if (gee) narratives.push(gee);
          if (doc) narratives.push(doc);
          break;
      }
    }

    return narratives;
  }

  /**
   * Get character dossier for player role
   */
  getCharacterDossier(role: PlayerRole) {
    return contentLoader.getCharacterDossier(role);
  }

  /**
   * Get ending narrative
   */
  getEndingNarrative(endingType: string) {
    return contentLoader.getEndingNarrative(endingType);
  }

  /**
   * Determine if narrative should auto-advance
   */
  shouldAutoAdvance(narrative: Narrative): boolean {
    // Auto-advance for short narratives (< 200 chars)
    return narrative.text.length < 200;
  }

  /**
   * Calculate reading time for narrative
   */
  getReadingTime(narrative: Narrative): number {
    // Average reading speed: 200 words per minute
    // Average word length: 5 characters
    const words = narrative.text.length / 5;
    const minutes = words / 200;
    const seconds = minutes * 60;

    // Min 3 seconds, max 30 seconds
    return Math.max(3000, Math.min(30000, seconds * 1000));
  }

  /**
   * Create narrative sequence for stage transition
   */
  createStageTransitionSequence(
    _fromStage: StageId,
    toStage: StageId,
    gameState: GameState
  ): NarrativeSequence {
    const narratives = this.getStageEntryNarratives(
      toStage,
      gameState.currentVariant || undefined,
      gameState
    );

    return {
      narratives,
      autoAdvance: false,
      delayMs: 1000
    };
  }

  /**
   * Get context-appropriate greeting
   */
  getGreeting(huntingPermitDay: number): string {
    if (huntingPermitDay === 0) return 'Mission begins...';
    if (huntingPermitDay === 6) return 'Final day of the permit!';
    return `Day ${huntingPermitDay} of 6`;
  }

  /**
   * Get energy status message
   */
  getEnergyMessage(energy: number): string {
    if (energy === 0) return 'CRITICAL: No energy remaining!';
    if (energy <= 20) return 'DANGER: Energy critically low!';
    if (energy <= 40) return 'WARNING: Low energy levels';
    if (energy <= 60) return 'Energy levels stable';
    return 'Energy optimal';
  }

  /**
   * Get stage location name
   */
  getStageLocation(stage: StageId): string {
    const locations: Record<StageId, string> = {
      0: 'USS Follett (Mothership)',
      1: 'Ocean Platform A-51',
      2: 'Cave of Noorah',
      3: 'Ocean Depths',
      4: 'Ocean Bottom / Debris Field',
      5: 'Ascending to Surface'
    };

    return locations[stage];
  }

  /**
   * Get exit narratives for stage
   */
  getStageExitNarratives(stage: StageId, gameState?: GameState): Narrative[] {
    const narrativeSegments = contentLoader.getNarrativeSegments(stage, 'onExit');
    const narratives = narrativeSegments.map(segment =>
      contentLoader.narrativeSegmentToNarrative(segment)
    );
    this.attachVideos(narratives, stage, 'onExit', gameState);
    return narratives;
  }

  /**
   * Check if stage has exit narratives
   */
  hasExitNarratives(stage: StageId): boolean {
    const narrativeSegments = contentLoader.getNarrativeSegments(stage, 'onExit');
    return narrativeSegments.length > 0;
  }

  /**
   * Get transition summary for stage change
   */
  getTransitionSummary(fromStage: StageId, toStage: StageId): string {
    const transitions: Record<string, string> = {
      '0-1': 'Teleporting to Platform A-51...',
      '1-2': 'Descending to the Cave of Noorah...',
      '2-3': 'Descending to the ocean floor...',
      '3-4': 'Preparing for the mission...',
      '4-5': 'Beginning the ascent...'
    };

    return transitions[`${fromStage}-${toStage}`] || 'Transitioning to next stage...';
  }
}

// Singleton instance
export const narrativeManager = new NarrativeManager();
